/**
 * Profile Modal Component
 * Displays and allows editing of current user profile
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useViewOnly } from '../../contexts';
import './Modal.css';

const ProfileModal = ({ isOpen, onClose, language }) => {
  const { user, authFetch, refreshUser } = useAuth();
  const { isViewOnly } = useViewOnly();
  const viewOnlyTip = language === 'he'
    ? 'מצב צפייה בלבד פעיל — בטל את המצב כדי לבצע שינויים'
    : 'View-only mode is on — disable to make changes';
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Translations
  const t = {
    title: language === 'he' ? 'הפרופיל שלי' : 'My Profile',
    firstName: language === 'he' ? 'שם פרטי' : 'First Name',
    lastName: language === 'he' ? 'שם משפחה' : 'Last Name',
    email: language === 'he' ? 'אימייל' : 'Email',
    role: language === 'he' ? 'תפקיד' : 'Role',
    lastLogin: language === 'he' ? 'התחברות אחרונה' : 'Last Login',
    save: language === 'he' ? 'שמור שינויים' : 'Save Changes',
    cancel: language === 'he' ? 'ביטול' : 'Cancel',
    saving: language === 'he' ? 'שומר...' : 'Saving...',
    savedSuccess: language === 'he' ? 'הפרופיל עודכן בהצלחה' : 'Profile updated successfully',
    errorSaving: language === 'he' ? 'שגיאה בשמירת הפרופיל' : 'Error saving profile'
  };

  // Role translations
  const roleLabels = {
    admin: language === 'he' ? 'מנהל' : 'Admin',
    doctor: language === 'he' ? 'רופא' : 'Doctor',
    receptionist: language === 'he' ? 'קבלה' : 'Receptionist',
    assistant: language === 'he' ? 'עוזר' : 'Assistant'
  };

  // Load user data when modal opens
  useEffect(() => {
    if (isOpen && user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || ''
      });
      setError('');
      setSuccess('');
    }
  }, [isOpen, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await authFetch('/api/auth/me', {
        method: 'PUT',
        body: JSON.stringify({
          first_name: formData.first_name,
          last_name: formData.last_name
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(t.savedSuccess);
        // Refresh user data in context
        if (refreshUser) {
          await refreshUser();
        }
      } else {
        setError(data.error || data.detail?.error || t.errorSaving);
      }
    } catch (err) {
      setError(t.errorSaving);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-medium" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t.title}</h2>
          <button className="close-btn" onClick={onClose} title={language === 'he' ? 'סגור' : 'Close'}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <div className="form-group">
            <label htmlFor="first_name">{t.firstName}</label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="last_name">{t.lastName}</label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">{t.email}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              disabled
              className="input-disabled"
            />
            <small className="form-hint">
              {language === 'he' ? 'לא ניתן לשנות את האימייל' : 'Email cannot be changed'}
            </small>
          </div>

          <div className="form-group">
            <label>{t.role}</label>
            <div className="info-value">{roleLabels[user?.role] || user?.role}</div>
          </div>

          {user?.last_login && (
            <div className="form-group">
              <label>{t.lastLogin}</label>
              <div className="info-value">
                {new Date(user.last_login).toLocaleString(language === 'he' ? 'he-IL' : 'en-US')}
              </div>
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t.cancel}
            </button>
            <button
              type="submit"
              className={`btn btn-primary ${isViewOnly ? 'disabled-view-only' : ''}`}
              disabled={loading || isViewOnly}
              title={isViewOnly ? viewOnlyTip : undefined}
            >
              {loading ? t.saving : t.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileModal;
